/**
 * Shared PDF Styling Constants
 *
 * Centralised CSS styles for PDF generation used by both:
 * - pdfRenderer.ts (single artefact PDF export)
 * - batchExport.ts (multi-artefact batch export)
 *
 * This ensures consistent styling across all PDF exports and reduces
 * maintenance burden when design changes are needed.
 */

/**
 * Brand colour for headings - matches WeBuild design system
 */
export const PDF_HEADING_COLOUR = "#2E74B5";

/**
 * Primary brand colour for titles
 */
export const PDF_PRIMARY_COLOUR = "#28326E";

/**
 * Text colour for body content (black for optimal print contrast)
 */
export const PDF_TEXT_COLOUR = "#000";

/**
 * Default font family stack for PDF documents
 * Uses Poppins with Arial fallback for reliability in PDF rendering contexts.
 */
export const PDF_DEFAULT_FONT_FAMILY = "Poppins, Arial, sans-serif";

/**
 * Default monospace font for code blocks
 */
export const PDF_DEFAULT_CODE_FONT_FAMILY = '"Courier New", monospace';

/**
 * Base font size in points
 */
export const PDF_BASE_FONT_SIZE = "11pt";

/**
 * Code font size in points
 */
export const PDF_CODE_FONT_SIZE = "9pt";

/**
 * Inline code font size in points
 */
export const PDF_INLINE_CODE_FONT_SIZE = "10pt";

/**
 * CSS for page break handling - aggressive rules to prevent content clipping
 *
 * IMPORTANT: html2pdf.js has a fundamental limitation where it renders HTML to a canvas
 * and slices it into pages. Without aggressive page-break-inside: avoid rules, content
 * gets clipped with black bar artifacts at page boundaries.
 *
 * Trade-off: These rules may cause occasional blank pages when large elements don't fit,
 * but this is preferable to unreadable clipped content with black bars.
 */
export const PDF_PAGE_BREAK_CSS = `
  /* Prevent page breaks inside elements to avoid canvas slicing artifacts */
  p, li, h1, h2, h3, h4, h5, h6, tr, blockquote, pre, code, ul, ol {
    page-break-inside: avoid;
  }

  /* Keep headings with their following content */
  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid;
  }

  /* Prevent orphaned headings and content */
  p {
    orphans: 3;
    widows: 3;
  }

  /* Section containers should stay together */
  .section-keep-together {
    page-break-inside: avoid;
  }
`;

/**
 * CSS for table styling in PDFs
 */
export const PDF_TABLE_CSS = `
  /* Table styling for proper column separation */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 12pt 0;
    table-layout: fixed;
  }

  thead {
    background-color: #f5f5f5;
  }

  th, td {
    border: 1pt solid #ddd;
    padding: 6pt 8pt;
    text-align: left;
    word-wrap: break-word;
  }

  th {
    font-weight: bold;
    background-color: #f0f0f0;
  }

  tr:nth-child(even) {
    background-color: #fafafa;
  }
`;

/**
 * CSS for list styling in PDFs - handles nested lists with proper markers
 */
export const PDF_LIST_CSS = `
  ul, ol {
    margin: 0 0 8pt 0;
    padding-left: 20pt;
  }

  ol {
    list-style-type: decimal;
  }

  ol ol {
    list-style-type: lower-alpha;
  }

  ol ol ol {
    list-style-type: lower-roman;
  }

  ul ul {
    margin: 0;
  }

  li {
    margin: 0 0 4pt 0;
    padding: 0;
    line-height: 1.4;
    display: list-item;
    vertical-align: top;
  }

  /* Use native list-style with adjusted font metrics */
  ul {
    list-style-type: none;
    padding-left: 20pt;
    margin: 0 0 8pt 0;
  }

  ul > li {
    position: relative;
    padding-left: 14pt;
  }

  ul > li::before {
    content: "\\2022";
    position: absolute;
    left: 0;
    top: 0.1em;
    font-size: 1em;
  }

  ul ul {
    padding-left: 16pt;
    margin: 4pt 0 0 0;
  }

  ul ul > li::before {
    content: "\\25E6";
  }

  ul ul ul {
    padding-left: 16pt;
  }

  ul ul ul > li::before {
    content: "\\25AA";
    font-size: 0.9em;
  }

  /* Level 4+ nesting */
  ul ul ul ul {
    padding-left: 16pt;
  }

  ul ul ul ul > li::before {
    content: "\\2022";
    font-size: 0.9em;
  }

  ul ul ul ul ul {
    padding-left: 16pt;
  }

  ul ul ul ul ul > li::before {
    content: "\\25E6";
    font-size: 0.9em;
  }

  ul ul ul ul ul ul {
    padding-left: 16pt;
  }

  ul ul ul ul ul ul > li::before {
    content: "\\25AA";
    font-size: 0.85em;
  }

  /* Ordered lists - use counter for consistent alignment */
  ol {
    list-style-type: none;
    padding-left: 20pt;
    margin: 0 0 8pt 0;
    counter-reset: ol-counter;
  }

  ol > li {
    position: relative;
    padding-left: 18pt;
    counter-increment: ol-counter;
  }

  ol > li::before {
    content: counter(ol-counter) ".";
    position: absolute;
    left: 0;
    top: 0;
    font-size: 1em;
    font-weight: normal;
  }

  /* Nested ordered lists */
  ol ol {
    counter-reset: ol-counter-2;
    padding-left: 16pt;
    margin: 4pt 0 0 0;
  }

  ol ol > li {
    counter-increment: ol-counter-2;
  }

  ol ol > li::before {
    content: counter(ol-counter-2, lower-alpha) ".";
  }

  ol ol ol {
    counter-reset: ol-counter-3;
    padding-left: 16pt;
  }

  ol ol ol > li {
    counter-increment: ol-counter-3;
  }

  ol ol ol > li::before {
    content: counter(ol-counter-3, lower-roman) ".";
  }
`;

/**
 * Generate CSS for code blocks in PDFs
 * @param codeFontFamily - Optional code font family from brand configuration
 * @returns CSS string for code styling
 */
export const getPdfCodeCss = (codeFontFamily?: string): string => {
  const codeFont = codeFontFamily || PDF_DEFAULT_CODE_FONT_FAMILY;

  return `
  code {
    font-family: ${codeFont};
    font-size: ${PDF_INLINE_CODE_FONT_SIZE};
    background-color: #f5f5f5;
    padding: 2px 4px;
    border-radius: 3px;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }

  pre {
    font-family: ${codeFont};
    font-size: ${PDF_CODE_FONT_SIZE};
    background-color: #f5f5f5;
    padding: 10pt;
    margin: 8pt 0;
    border-left: 3px solid ${PDF_HEADING_COLOUR};
    overflow-x: auto;
  }
`;
};

/**
 * Legacy constant for backward compatibility
 * @deprecated Use getPdfCodeCss(codeFontFamily) instead
 */
export const PDF_CODE_CSS = getPdfCodeCss();

/**
 * Brand font options for PDF generation
 */
export interface PdfBrandFonts {
  fontFamily?: string;
  codeFontFamily?: string;
}

/**
 * Unique container class for PDF rendering
 * Used to scope all PDF styles and prevent them from affecting the main document
 * when html2pdf.js temporarily injects content into the DOM
 */
export const PDF_CONTAINER_CLASS = "pdf-render-container";

/**
 * Generate scoped page break CSS
 */
const getScopedPdfPageBreakCss = (scope: string): string => `
  /* Prevent page breaks inside elements to avoid canvas slicing artifacts */
  ${scope} p, ${scope} li, ${scope} h1, ${scope} h2, ${scope} h3, ${scope} h4, ${scope} h5, ${scope} h6, ${scope} tr, ${scope} blockquote, ${scope} pre, ${scope} code, ${scope} ul, ${scope} ol {
    page-break-inside: avoid;
  }

  /* Keep headings with their following content */
  ${scope} h1, ${scope} h2, ${scope} h3, ${scope} h4, ${scope} h5, ${scope} h6 {
    page-break-after: avoid;
  }

  /* Prevent orphaned headings and content */
  ${scope} p {
    orphans: 3;
    widows: 3;
  }

  /* Section containers should stay together */
  ${scope} .section-keep-together {
    page-break-inside: avoid;
  }
`;

/**
 * Generate scoped table CSS
 */
const getScopedPdfTableCss = (scope: string): string => `
  /* Table styling for proper column separation */
  ${scope} table {
    border-collapse: collapse;
    width: 100%;
    margin: 12pt 0;
    table-layout: fixed;
  }

  ${scope} thead {
    background-color: #f5f5f5;
  }

  ${scope} th, ${scope} td {
    border: 1pt solid #ddd;
    padding: 6pt 8pt;
    text-align: left;
    word-wrap: break-word;
  }

  ${scope} th {
    font-weight: bold;
    background-color: #f0f0f0;
  }

  ${scope} tr:nth-child(even) {
    background-color: #fafafa;
  }
`;

/**
 * Generate scoped list CSS
 */
const getScopedPdfListCss = (scope: string): string => `
  ${scope} ul, ${scope} ol {
    margin: 0 0 8pt 0;
    padding-left: 20pt;
  }

  ${scope} ol {
    list-style-type: decimal;
  }

  ${scope} ol ol {
    list-style-type: lower-alpha;
  }

  ${scope} ol ol ol {
    list-style-type: lower-roman;
  }

  ${scope} ul ul {
    margin: 0;
  }

  ${scope} li {
    margin: 0 0 4pt 0;
    padding: 0;
    line-height: 1.4;
    display: list-item;
    vertical-align: top;
  }

  /* Use native list-style with adjusted font metrics */
  ${scope} ul {
    list-style-type: none;
    padding-left: 20pt;
    margin: 0 0 8pt 0;
  }

  ${scope} ul > li {
    position: relative;
    padding-left: 14pt;
  }

  ${scope} ul > li::before {
    content: "\\2022";
    position: absolute;
    left: 0;
    top: 0.1em;
    font-size: 1em;
  }

  ${scope} ul ul {
    padding-left: 16pt;
    margin: 4pt 0 0 0;
  }

  ${scope} ul ul > li::before {
    content: "\\25E6";
  }

  ${scope} ul ul ul {
    padding-left: 16pt;
  }

  ${scope} ul ul ul > li::before {
    content: "\\25AA";
    font-size: 0.9em;
  }

  /* Level 4+ nesting */
  ${scope} ul ul ul ul {
    padding-left: 16pt;
  }

  ${scope} ul ul ul ul > li::before {
    content: "\\2022";
    font-size: 0.9em;
  }

  ${scope} ul ul ul ul ul {
    padding-left: 16pt;
  }

  ${scope} ul ul ul ul ul > li::before {
    content: "\\25E6";
    font-size: 0.9em;
  }

  ${scope} ul ul ul ul ul ul {
    padding-left: 16pt;
  }

  ${scope} ul ul ul ul ul ul > li::before {
    content: "\\25AA";
    font-size: 0.85em;
  }

  /* Ordered lists - use counter for consistent alignment */
  ${scope} ol {
    list-style-type: none;
    padding-left: 20pt;
    margin: 0 0 8pt 0;
    counter-reset: ol-counter;
  }

  ${scope} ol > li {
    position: relative;
    padding-left: 18pt;
    counter-increment: ol-counter;
  }

  ${scope} ol > li::before {
    content: counter(ol-counter) ".";
    position: absolute;
    left: 0;
    top: 0;
    font-size: 1em;
    font-weight: normal;
  }

  /* Nested ordered lists */
  ${scope} ol ol {
    counter-reset: ol-counter-2;
    padding-left: 16pt;
    margin: 4pt 0 0 0;
  }

  ${scope} ol ol > li {
    counter-increment: ol-counter-2;
  }

  ${scope} ol ol > li::before {
    content: counter(ol-counter-2, lower-alpha) ".";
  }

  ${scope} ol ol ol {
    counter-reset: ol-counter-3;
    padding-left: 16pt;
  }

  ${scope} ol ol ol > li {
    counter-increment: ol-counter-3;
  }

  ${scope} ol ol ol > li::before {
    content: counter(ol-counter-3, lower-roman) ".";
  }
`;

/**
 * Generate scoped code CSS
 */
const getScopedPdfCodeCss = (scope: string, codeFontFamily?: string): string => {
  const codeFont = codeFontFamily || PDF_DEFAULT_CODE_FONT_FAMILY;

  return `
  ${scope} code {
    font-family: ${codeFont};
    font-size: ${PDF_INLINE_CODE_FONT_SIZE};
    background-color: #f5f5f5;
    padding: 2px 4px;
    border-radius: 3px;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }

  ${scope} pre {
    font-family: ${codeFont};
    font-size: ${PDF_CODE_FONT_SIZE};
    background-color: #f5f5f5;
    padding: 10pt;
    margin: 8pt 0;
    border-left: 3px solid ${PDF_HEADING_COLOUR};
    overflow-x: auto;
  }
`;
};

/**
 * Generate complete CSS stylesheet for full PDF document rendering
 * Used by pdfRenderer.ts for single-artefact exports
 *
 * IMPORTANT: All styles are scoped under .pdf-render-container to prevent
 * them from leaking into the main document during html2pdf.js rendering.
 *
 * @param brandFonts - Optional brand font configuration
 * @returns CSS stylesheet string
 */
export const getPdfFullStylesheet = (brandFonts?: PdfBrandFonts): string => {
  const fontFamily = brandFonts?.fontFamily || PDF_DEFAULT_FONT_FAMILY;
  const scope = `.${PDF_CONTAINER_CLASS}`;

  return `
  ${scope} {
    font-family: ${fontFamily};
    font-size: ${PDF_BASE_FONT_SIZE};
    line-height: 1.6;
    color: ${PDF_TEXT_COLOUR};
    margin: 0;
    padding: 20px;
  }

  ${scope} h1 {
    font-size: 24pt;
    font-weight: bold;
    margin: 16pt 0 8pt 0;
    color: ${PDF_HEADING_COLOUR};
  }

  ${scope} h2 {
    font-size: 20pt;
    font-weight: bold;
    margin: 14pt 0 7pt 0;
    color: ${PDF_HEADING_COLOUR};
  }

  ${scope} h3 {
    font-size: 16pt;
    font-weight: bold;
    margin: 12pt 0 6pt 0;
    color: ${PDF_HEADING_COLOUR};
  }

  ${scope} p {
    margin: 0 0 8pt 0;
  }

  ${getScopedPdfListCss(scope)}

  ${scope} strong, ${scope} b {
    font-weight: bold;
  }

  ${scope} em, ${scope} i {
    font-style: italic;
  }

  ${getScopedPdfCodeCss(scope, brandFonts?.codeFontFamily)}

  ${scope} blockquote {
    margin: 8pt 0;
    padding-left: 10pt;
    border-left: 3px solid #ccc;
    color: #666;
    font-style: italic;
  }

  ${scope} a {
    color: #0066cc;
    text-decoration: underline;
  }

  ${getScopedPdfTableCss(scope)}

  ${getScopedPdfPageBreakCss(scope)}
`;
};

/**
 * Legacy constant for backward compatibility
 * @deprecated Use getPdfFullStylesheet(brandFontFamily) instead
 */
export const PDF_FULL_STYLESHEET = getPdfFullStylesheet();

/**
 * Generate inline style for batch export HTML content wrapper
 * Used by batchExport.ts for multi-artefact exports
 * @param brandFontFamily - Optional font family from brand configuration
 * @returns Inline CSS style string
 */
export const getPdfBatchWrapperStyle = (brandFontFamily?: string): string => {
  const fontFamily = brandFontFamily || PDF_DEFAULT_FONT_FAMILY;

  return `font-family: ${fontFamily}; padding: 20px; color: ${PDF_TEXT_COLOUR};`;
};

/**
 * Legacy constant for backward compatibility
 * @deprecated Use getPdfBatchWrapperStyle(brandFontFamily) instead
 */
export const PDF_BATCH_WRAPPER_STYLE = getPdfBatchWrapperStyle();

/**
 * Style for batch export main title - formatted as prominent H1 heading
 */
export const PDF_BATCH_TITLE_STYLE = `font-size: 28px; font-weight: bold; color: ${PDF_PRIMARY_COLOUR}; border-bottom: 3px solid ${PDF_PRIMARY_COLOUR}; padding-bottom: 10px; margin-bottom: 20px;`;

/**
 * Style for batch export section headings
 */
export const PDF_BATCH_HEADING_STYLE = `color: ${PDF_PRIMARY_COLOUR};`;

/**
 * Style for batch export code blocks
 */
export const PDF_BATCH_CODE_STYLE = `background: #f5f5f5; padding: 15px; border-radius: 5px; overflow-x: auto; color: ${PDF_TEXT_COLOUR};`;

/**
 * Style for batch export content sections
 */
export const PDF_BATCH_CONTENT_STYLE = `margin: 15px 0; color: ${PDF_TEXT_COLOUR}; line-height: 1.6;`;

/**
 * html2pdf.js pagebreak configuration
 *
 * IMPORTANT: Using "avoid-all" mode to prevent content clipping at page boundaries.
 * This may cause occasional blank pages when large elements don't fit on a page,
 * but this is preferable to unreadable content with black bar artifacts.
 */
export const PDF_PAGEBREAK_CONFIG = {
  mode: ["avoid-all", "css", "legacy"] as const,
  avoid: [
    "p",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "tr",
    "blockquote",
    "pre",
    "code",
    "ul",
    "ol",
    ".section-keep-together",
  ],
};

/**
 * html2pdf.js pagebreak configuration with image support
 * Used for batch exports that include embedded images
 */
export const PDF_PAGEBREAK_CONFIG_WITH_IMAGES = {
  mode: ["avoid-all", "css", "legacy"] as const,
  avoid: [
    "p",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "tr",
    "blockquote",
    "pre",
    "code",
    "ul",
    "ol",
    "img",
    ".section-keep-together",
  ],
};
